import { toast } from "sonner";
import { EPISODES, IMG } from "@/data/content";
import { MAP_COORDS } from "@/data/mapCoords";
import { usePassport } from "@/context/PassportContext";

const points = EPISODES.map((e) => ({ ...e, ...MAP_COORDS[e.code] }));
const linePts = points.map((p) => `${p.x},${p.y}`).join(" ");

export const WorldMap = () => {
  const { isStamped, toggleStamp, count } = usePassport();

  const onStamp = (e) => {
    const was = isStamped(e.code);
    toggleStamp(e.code);
    if (!was) {
      toast.success(`¡Sello ${e.code} conseguido!`, { description: `${e.city} añadido a tu pasaporte.` });
    }
  };

  return (
    <div
      className="relative overflow-hidden rounded-[2rem] bg-white p-4 shadow-[0_8px_30px_rgb(0,0,0,0.05)] ring-1 ring-ink/5"
      data-testid="world-map"
    >
      {/* Encabezado */}
      <div className="flex items-center justify-between px-2 pb-2 pt-1">
        <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-coral">
          Ruta · EP 01 → EP 12
        </span>
        <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-ink/40">
          12 destinos · 1 temporada
        </span>
      </div>

      <div className="relative w-full overflow-hidden rounded-[1.25rem]" style={{ aspectRatio: "3 / 2" }}>
        {/* Mapamundi ilustrado */}
        <img
          src={IMG.worldMap}
          alt="Mapamundi ilustrado de la ruta"
          className="absolute inset-0 h-full w-full object-cover"
          data-testid="world-map-image"
        />
        {/* Ruta que conecta los destinos */}
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
          <polyline
            points={linePts}
            fill="none"
            stroke="#ffffff"
            strokeWidth="0.6"
            strokeOpacity="0.85"
            strokeDasharray="0.6 1.6"
            strokeLinecap="round"
          />
        </svg>

        {/* Puntos interactivos */}
        {points.map((e) => {
          const active = isStamped(e.code);
          return (
            <button
              key={e.code}
              type="button"
              onClick={() => onStamp(e)}
              data-testid={`map-point-${e.code}`}
              title={`${e.city} — clic para sellar`}
              className="group absolute -translate-x-1/2 -translate-y-1/2"
              style={{ left: `${e.x}%`, top: `${e.y}%` }}
            >
              {!active && (
                <span
                  className="absolute inset-0 -z-10 animate-ping rounded-full"
                  style={{ backgroundColor: e.color, opacity: 0.6 }}
                />
              )}
              <span
                className={`flex h-4 w-4 items-center justify-center rounded-full ring-2 ring-white transition-transform duration-200 group-hover:scale-125 sm:h-5 sm:w-5 ${
                  active ? "scale-110" : ""
                }`}
                style={{
                  backgroundColor: e.color,
                  boxShadow: `0 1px 4px rgba(0,0,0,0.4)${active ? `, 0 0 0 4px ${e.color}55` : ""}`,
                }}
              >
                {active && <span className="text-[9px] font-bold text-ink">✓</span>}
              </span>
              <span className="pointer-events-none absolute left-1/2 top-full mt-1 -translate-x-1/2 whitespace-nowrap rounded-md bg-ink px-1.5 py-0.5 font-mono text-[9px] font-bold text-cream opacity-0 transition-opacity group-hover:opacity-100">
                {e.n} · {e.city}
              </span>
            </button>
          );
        })}
      </div>

      {/* Leyenda */}
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 px-2 pb-1">
        {points.map((e) => (
          <span key={e.code} className="inline-flex items-center gap-1.5 font-mono text-[10px] text-ink/60">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: e.color }} />
            {e.n} {e.city}
          </span>
        ))}
      </div>
      <p className="px-2 pt-1 font-mono text-[10px] uppercase tracking-widest text-ink/40" data-testid="map-progress">
        Sellos conseguidos: {count} / 12 · toca un punto para sellar
      </p>
    </div>
  );
};
